import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SumsubWebSdk from '@sumsub/websdk-react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import api from '../../../../../utils/api'
import Loader, { LoaderMode } from '../../../../Loader'
import { getUserApps, getAppStatus } from '../../../../../redux/actions'
import Utils from '../../../../../utils/utils'
import { UserProfile } from '../../../../../utils/types'
import { useAppState, useSimplifyOnboardingCheck } from '../../../../../utils/hooks'
import { useOnboardingAnalytics } from '../../../Onboarding/useOnboardingAnalytics'

import styles from './style.module.scss'
import { ReactComponent as LockIcon } from './lock.svg'

interface SumSubProps {
    applicationID: number
    userProfile: UserProfile
    isMobile?: boolean
    onAlreadyVerified?: (completedOnLoad: boolean) => void
}

const SumSub: FC<SumSubProps> = ({ applicationID, userProfile, isMobile, onAlreadyVerified, children }) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const [accessToken, setAccessToken] = useState<string | null>(null)
    const [isInitialized, setIsInitialized] = useState<boolean>(false)
    const { redirectedFromWTR } = useAppState()
    const { checkForSimplifiedOnboarding } = useSimplifyOnboardingCheck()
    const { fireKycSubmitted } = useOnboardingAnalytics(checkForSimplifiedOnboarding())
    const kycSubmittedFired = useRef(false)
    const applicantSubmittedRef = useRef(false)

    const getAccessToken = useCallback(async () => {
        try {
            const response = await api.getSumSubAccessToken(applicationID)
            if (Utils.validResponse(response)) {
                const token: string = response.payload[0].result
                return token
            } else {
                return response.payload[0].result.message || ''
            }
        } catch (error) {
            return 'Unhandled error'
        }
    }, [applicationID])

    useEffect(() => {
        const getData = async () => {
            const token = await getAccessToken()
            setIsInitialized(true)
            setAccessToken(token)
        }

        if (applicationID) {
            getData()
        }
    }, [applicationID, getAccessToken])

    const langCode = useMemo(() => {
        switch (userProfile.preferredLanguage.language_code) {
            case 'zh-Hans':
                return 'zh'
            case 'zh-Hant':
                return 'zh-tw'
            default:
                return userProfile.preferredLanguage.language_code
        }
    }, [userProfile])

    const mobileStyles = {
        height: `${window.innerHeight}px`,
        overflow: 'scroll',
    }

    return (
        <div
            className={styles.sumsub}
            style={isMobile ? mobileStyles : undefined}
        >
            {!isInitialized && <Loader mode={LoaderMode.StandAlone} />}
            {accessToken && (
                <>
                    <SumsubWebSdk
                        accessToken={accessToken}
                        expirationHandler={getAccessToken}
                        config={{
                            lang: langCode,
                            country: userProfile?.country?.code3,
                            uiConf: {
                                customCss: `${window.origin}/sumsub.css`,
                                scrollIntoView: redirectedFromWTR ? false : true,
                            }
                        }}
                        options={{ addViewportTag: false, adaptIframeHeight: true }}
                        onMessage={(type, payload: any) => {
                            console.log('onMessage', type, payload)
                            if (type === 'idCheck.onApplicantSubmitted') {
                                applicantSubmittedRef.current = true
                                if (!kycSubmittedFired.current) {
                                    kycSubmittedFired.current = true
                                    fireKycSubmitted()
                                }
                            }
                            if (type === 'idCheck.onApplicantStatusChanged'
                                && payload?.reviewStatus === 'completed') {
                                onAlreadyVerified?.(!applicantSubmittedRef.current)
                            }
                            if (type === 'idCheck.applicantStatus' || type === 'idCheck.onApplicantSubmitted' || type === 'idCheck.onApplicantStatusChanged') {
                                setTimeout(() => {
                                    if (checkForSimplifiedOnboarding()) {
                                        window.dataLayer?.push({
                                            'event': 'custom_event',
                                            'event_name': 'simplified_level3_verify_identity',
                                        })
                                    }
                                    dispatch(getAppStatus(applicationID))
                                    dispatch(getUserApps())
                                    if (redirectedFromWTR) {
                                        Utils.postMessageToWTR({ message: 'tradePage' })
                                    }
                                }, 500)
                            }
                        }}
                        onError={(error) => {
                            console.log('sumsub onError', error)
                        }}
                    />
                    <div className={styles.sslNotice}>
                        <LockIcon className={styles.sslIcon} aria-hidden />
                        <span>{t('account:sumsub.sslNotice')}</span>
                    </div>
                    {children}
                </>
            )}
        </div>
    )
}

export default SumSub
